import React, { useMemo } from "react"
import { StyleSheet, View, ViewStyle } from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { Image } from "expo-image"

import { useAppTheme } from "src/hooks/useAppTheme"
import { useWebsiteThumbnail } from "src/hooks/useWebsiteThumbnail"
import { ThemedStyle } from "src/theme"
import { ContentLibraryItem } from "./adapters"
import { cmModeIconMap } from "./cmModeIcons"

// Aspect ratio: 16:9 — standard video thumbnail
export const THUMBNAIL_ASPECT_RATIO = 16 / 9

// Fallback icon names per media type. Website items use mediaType "url".
const FALLBACK_ICON: Record<
  string,
  "picture-as-pdf" | "headset" | "image" | "language" | "description"
> = {
  pdf: "picture-as-pdf",
  audio: "headset",
  image: "image",
  url: "language",
}

interface ThumbnailCardProps {
  item: ContentLibraryItem
  /** Width of the card — height is derived from 16:9 ratio */
  width: number
}

/**
 * A 16:9 thumbnail card for a content library item.
 *
 * Fallback chain:
 * 1. backend thumbnail_url
 * 2. cm_mode_icons asset (Dan's lecture/discovery icons, when provided)
 * 3. Material icon for the media type
 */
export const ThumbnailCard = React.memo(function ThumbnailCard({
  item,
  width,
}: ThumbnailCardProps) {
  const { themed, theme } = useAppTheme()
  const height = Math.round(width / THUMBNAIL_ASPECT_RATIO)

  const isVideo = item.mediaType === "video"

  // For website items without a backend thumbnail, derive one from Open Graph
  // metadata (same source the chat-history website preview uses).
  const websiteUrlToFetch = item.mediaType === "url" && !item.thumbnailUrl ? item.mediaUrl : null
  const { imageUrl: websiteImageUrl } = useWebsiteThumbnail(websiteUrlToFetch)

  // Video thumbnail generation via ThumbnailQueueProvider is auth-only.
  // Use the backend thumbnail_url first, then the website OG image; icon fallback when absent.
  const resolvedThumbnailUri = item.thumbnailUrl ?? websiteImageUrl ?? null

  const cardStyle: ViewStyle = { width, height, borderRadius: 12, overflow: "hidden" }
  const iconName = item.mediaType ? FALLBACK_ICON[item.mediaType] : "description"

  const cmModeIconSource = item.mediaType ? cmModeIconMap[item.mediaType] : null

  const fallbackIconColor = theme.colors.sectionSeparator ?? "#808080"

  // Stable source objects prevent expo-image from treating same-URI images as
  // changed (which would cause a brief decode flash even with a warm cache).
  const thumbnailSource = useMemo(
    () => (resolvedThumbnailUri ? { uri: resolvedThumbnailUri } : null),
    [resolvedThumbnailUri],
  )

  return (
    <View style={[cardStyle, themed($container)]}>
      {thumbnailSource ? (
        <Image
          source={thumbnailSource}
          style={StyleSheet.absoluteFill}
          contentFit={item.mediaType === "pdf" ? "cover" : "contain"}
          contentPosition={item.mediaType === "pdf" ? "top" : undefined}
          recyclingKey={`cl-thumb-${item.id}`}
          cachePolicy="memory-disk"
        />
      ) : cmModeIconSource ? (
        <Image
          source={cmModeIconSource}
          style={StyleSheet.absoluteFill}
          contentFit="cover"
          recyclingKey={`cl-mode-icon-${item.id}`}
        />
      ) : (
        <View style={[StyleSheet.absoluteFill, themed($fallback)]}>
          <MaterialIcons
            name={iconName ?? "description"}
            size={Math.round(height * 0.4)}
            color={fallbackIconColor}
          />
        </View>
      )}

      {/* Play icon overlay for videos */}
      {isVideo && (
        <View style={styles.playOverlay} pointerEvents="none">
          <View style={styles.playIconBg}>
            <MaterialIcons name="play-arrow" size={28} color="#fff" />
          </View>
        </View>
      )}
    </View>
  )
})

const styles = StyleSheet.create({
  playOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "flex-end",
    justifyContent: "flex-end",
    padding: 8,
  },
  playIconBg: {
    backgroundColor: "rgba(0,0,0,0.6)",
    borderRadius: 20,
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
  },
})

const $container: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.contentCardTopicBackground,
  alignItems: "center",
  justifyContent: "center",
})

const $fallback: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.contentCardTopicBackground,
  alignItems: "center",
  justifyContent: "center",
})
